<script setup lang="ts">
import { computed, onMounted, ref, useId, watch } from 'vue'

import UiButton from '@/components/UiButton.vue'
import type { PolicySpec, ProductSpec } from '@/api/ls'
import { generateUniqueValue } from '@/lib/generatedValue'

/**
 * 行為參數編輯器（批110；工單 #5494／#5495 的 UI 那一半）。
 *
 * 與 `QuotaEditor.vue` 同構、同一套 props（`modelValue` ＋ `spec`），差別只有三件事：
 *
 * 1. **三種型別**：`number`（帶 min/max 的數字欄）／`bool`（開關）／`string`（下拉，
 *    選項來自憑證設計的 `options`）。
 * 2. **沒有「無上限」的概念**——policy 是「這套站台的行為參數」（寬限幾天、某個模式開不開），
 *    不是數量上限。留空＝不帶這個鍵＝產品端吃 `absent_default`。
 * 3. **值域由憑證設計宣告**（`min`／`max`／`options`）。這裡只是先擋一手讓人填得舒服，
 *    真正的校驗在後端 `services/products.validate_policy_values`——批110 之前那組
 *    min/max 只是裝飾，API 直接送 3650 照樣簽得出去。
 *
 * ★ 未知鍵（憑證設計沒宣告、但證上有）**原樣保留不靜默刪**——與 ModulePicker／QuotaEditor
 *   同一條鐵律：靜默刪鍵＝客戶買的東西無聲消失，而畫面上完全看不出來。
 * ★ `initFrom` 只在掛載時跑 → 父層帶入新值（換套裝／換產品）一律換 `:key` 重掛。
 *
 * 批151（裁示 112）：字串參數標了「可產生唯一值」（`generatable`）時改成**文字輸入**
 * （與可選值不可並存，後端宣告時就擋；舊資料若兩者都有，不在清單裡的值標紅），
 * 旁邊一顆「產生」鈕填入 `YYYYMMDD-xxxx`
 * （`lib/generatedValue.ts`）。**只在按下時產生、不自動填**；按下同時把這個鍵勾成「帶入」。
 * 本元件用在直簽、換發、審核、代管核可與開站精靈——改這裡五處一起生效。
 */
const props = defineProps<{
  modelValue: Record<string, unknown> | null
  /** 該產品的憑證設計；null＝尚未載入（顯示提示、原樣回吐 modelValue）。 */
  spec: ProductSpec | null
}>()
const emit = defineEmits<{ 'update:modelValue': [Record<string, unknown>] }>()

const fields = computed(() => props.spec?.policies ?? [])

/** 每個鍵的草稿值（一律存字串，送出時再依 type 轉回去）。 */
const draft = ref<Record<string, string>>({})
/** 這個鍵要不要帶進 payload（關掉＝讓產品端吃 `absent_default`）。 */
const on = ref<Record<string, boolean>>({})
/** 憑證設計沒宣告、但證上有的鍵——原樣保留。 */
const unknownEntries = ref<Record<string, unknown>>({})

function initFrom(p: Record<string, unknown> | null) {
  const known = new Set<string>()
  for (const f of fields.value) {
    known.add(f.key)
    const v = p?.[f.key]
    const has = v !== undefined && v !== null
    on.value[f.key] = has
    draft.value[f.key] = has ? String(v) : defaultText(f.key)
  }
  const unknown: Record<string, unknown> = {}
  for (const [k, v] of Object.entries(p ?? {})) {
    if (!known.has(k)) unknown[k] = v
  }
  unknownEntries.value = unknown
}

/** 沒帶值時輸入框顯示什麼：優先用憑證設計的 `absent_default`，再退到型別的零值。 */
function defaultText(key: string): string {
  const f = fields.value.find((x) => x.key === key)
  if (f?.absent_default != null) return String(f.absent_default)
  if (f?.type === 'bool') return 'false'
  if (f?.type === 'string') return f.options?.[0] ?? ''
  return ''
}

initFrom(props.modelValue)
watch(
  () => props.spec,
  () => {
    initFrom(props.modelValue)
    emitAll()
  },
)
onMounted(() => emitAll())

function emitAll() {
  // spec 還沒到＝不知道有哪些鍵 → 原樣回吐（絕不清空成 {}）。
  if (!fields.value.length) {
    emit('update:modelValue', { ...(props.modelValue ?? {}) })
    return
  }
  const out: Record<string, unknown> = {}
  for (const f of fields.value) {
    if (!on.value[f.key]) continue
    const raw = draft.value[f.key] ?? ''
    if (f.type === 'number') {
      const n = Number(raw)
      if (raw !== '' && Number.isFinite(n)) out[f.key] = n
    } else if (f.type === 'bool') {
      out[f.key] = raw === 'true'
    } else if (raw !== '') {
      out[f.key] = raw
    }
  }
  emit('update:modelValue', { ...out, ...unknownEntries.value })
}

function toggle(key: string) {
  on.value[key] = !on.value[key]
  emitAll()
}

/** 超出憑證設計宣告的值域 → 紅字（後端會 422，先在這裡講）。 */
function outOfRange(key: string): boolean {
  const f = fields.value.find((x) => x.key === key)
  if (!f || f.type !== 'number' || !on.value[key]) return false
  const n = Number(draft.value[key])
  if (!Number.isFinite(n)) return true
  if (f.min != null && n < f.min) return true
  return f.max != null && n > f.max
}

function rangeText(key: string): string {
  const f = fields.value.find((x) => x.key === key)
  if (!f || f.type !== 'number') return ''
  if (f.min != null && f.max != null) return `${f.min}–${f.max}`
  if (f.max != null) return `≤ ${f.max}`
  return f.min != null ? `≥ ${f.min}` : ''
}

/** 可產生唯一值的字串參數（文字輸入＋「產生」鈕）。 */
function generatable(f: PolicySpec): boolean {
  return f.type === 'string' && f.generatable === true
}

/** 按「產生」：填入一個新值並勾成「帶入」（不自動填——只有按下才產生）。 */
function generate(key: string) {
  draft.value[key] = generateUniqueValue()
  on.value[key] = true
  emitAll()
}

/** 自由字串但憑證設計仍列了可選值：不在清單裡的值後端會 422 `not_in_options`，先講。 */
function notInOptions(key: string): boolean {
  const f = fields.value.find((x) => x.key === key)
  if (!f || !generatable(f) || !on.value[key] || !(f.options ?? []).length) return false
  const v = draft.value[key] ?? ''
  return v !== '' && !(f.options ?? []).includes(v)
}

/** datalist 的 id（同一頁可能同時有兩個編輯器，加上實例 id 避免撞名）。 */
const uid = useId()
</script>

<template>
  <div>
    <p v-if="!fields.length" class="pe-empty">
      這個產品還沒有定義任何行為參數——請先到〈產品 → 憑證設計〉新增。
    </p>
    <div class="pe">
      <div v-for="f in fields" :key="f.key" class="pe-row" :class="{ dim: f.deprecated }">
        <label class="pe-on" :class="{ off: !on[f.key] }">
          <input type="checkbox" :checked="on[f.key]" @change="toggle(f.key)" />
          <span :title="f.hint ?? f.key">
            {{ f.label }}<small v-if="f.deprecated" class="dep">（已停用）</small>
          </span>
        </label>

        <select
          v-if="f.type === 'bool'"
          v-model="draft[f.key]"
          class="pe-in"
          :disabled="!on[f.key]"
          @change="emitAll"
        >
          <option value="true">開啟</option>
          <option value="false">關閉</option>
        </select>
        <template v-else-if="generatable(f)">
          <input
            v-model="draft[f.key]"
            class="pe-in mono"
            :class="{ bad: notInOptions(f.key) }"
            :list="f.options?.length ? `${uid}-${f.key}` : undefined"
            :disabled="!on[f.key]"
            maxlength="200"
            @input="emitAll"
          />
          <datalist v-if="f.options?.length" :id="`${uid}-${f.key}`">
            <option v-for="o in f.options" :key="o" :value="o" />
          </datalist>
        </template>
        <select
          v-else-if="f.type === 'string'"
          v-model="draft[f.key]"
          class="pe-in"
          :disabled="!on[f.key]"
          @change="emitAll"
        >
          <option v-for="o in f.options ?? []" :key="o" :value="o">{{ o }}</option>
        </select>
        <input
          v-else
          v-model="draft[f.key]"
          type="number"
          class="pe-in"
          :class="{ bad: outOfRange(f.key) }"
          :min="f.min ?? undefined"
          :max="f.max ?? undefined"
          :disabled="!on[f.key]"
          @input="emitAll"
        />

        <div class="pe-side">
          <UiButton
            v-if="generatable(f)"
            type="button"
            size="sm"
            variant="secondary"
            class="pe-gen"
            title="填入一個以今天日期開頭的唯一值（YYYYMMDD-xxxx）"
            @click="generate(f.key)"
          >
            產生
          </UiButton>
          <small class="pe-hint">
            <span v-if="outOfRange(f.key)" class="bad-text">可用範圍 {{ rangeText(f.key) }}</span>
            <span v-else-if="notInOptions(f.key)" class="bad-text">不在可選值內（{{ (f.options ?? []).join('、') }}）</span>
            <span v-else-if="!on[f.key]">
              不帶此參數（產品端用預設 {{ f.absent_default ?? '—' }}）
            </span>
            <span v-else-if="rangeText(f.key)">{{ rangeText(f.key) }}</span>
          </small>
        </div>
      </div>
    </div>
    <p v-if="Object.keys(unknownEntries).length" class="pe-unknown">
      這張授權另有 {{ Object.keys(unknownEntries).join('、') }}
      ——這個產品的憑證設計沒有定義，原樣保留。
    </p>
  </div>
</template>

<style scoped>
.pe {
  display: grid;
  gap: 8px;
}
.pe-row {
  display: grid;
  /* 批151：值欄 120→150px——「產生」填入的 `YYYYMMDD-xxxx`（等寬 13 字）在 120px 會被截掉。 */
  grid-template-columns: 1fr 150px minmax(0, 1.2fr);
  gap: 10px;
  align-items: center;
}
.pe-row.dim {
  opacity: 0.6;
}
.pe-on {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: var(--fs-13);
  cursor: pointer;
}
.pe-on.off span {
  color: var(--fg-4);
}
.pe-in {
  width: 100%;
  min-width: 0;
}
.pe-in.bad {
  border-color: var(--danger);
}
.pe-side {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.pe-gen {
  flex: none;
}
.pe-hint {
  color: var(--fg-4);
  font-size: var(--fs-11);
}
.mono {
  font-family: var(--font-mono);
}
.bad-text {
  color: var(--danger);
}
.dep {
  color: var(--fg-4);
}
.pe-empty,
.pe-unknown {
  margin: 6px 0 0;
  color: var(--fg-3);
  font-size: var(--fs-12);
}
</style>
